import { atom } from 'jotai'

/**
 * A single, always-mounted overlay surface that covers the workspace
 * main-content panel without a backdrop. Designed to host HITL
 * preview/edit experiences:
 *
 *   - `vault-document-draft` (#364): Vera-generated document the user
 *     reviews, edits, and either saves to vault or discards.
 *
 *   - Future (#345): in-chat preview overlays for controls, tests,
 *     policy sections, claims, evidence, document references. Add a
 *     new `kind` variant + a renderer entry in `MainPanelOverlay`.
 *
 * Single-slot model: `open` replaces the active entry. There is never
 * more than one overlay at a time. If the user is mid-edit on overlay
 * A and the agent triggers overlay B, B replaces A and the chat card
 * for A flips to its "pending — reopen" state. Stack-management is
 * intentionally out of scope (kept the same model the user asked for:
 * one overlay, content swaps in place).
 *
 * The overlay container is always mounted in the layout — its
 * visibility is driven by `mainPanelOverlayActiveAtom`. This keeps the
 * containing layout's JSX tree stable across open/close transitions
 * (no remounts, animation-friendly).
 */

export interface VaultDocumentDraftOverlayPayload {
  documentId?: string
  originalDocumentTitle?: string
  expectedUpdatedAt?: string
  keyDocumentKey?: string
  /** Stable id (typically the chat tool call id). */
  draftId: string
  title: string
  content: string
  tiptapJson?: unknown | null
  category: string
  description?: string
  controlRefs?: string[]
  controlDefIds?: string[]
  effectiveFrom?: string | null
  effectiveTo?: string | null
  reviewedAt?: string | null
  sourceChatId?: string | null
  sourceMessageId?: string | null
  /**
   * Called after the user clicks Save and the tRPC mutation resolves.
   * The draft id and the saved documentId are passed back so the chat
   * card can transition to "saved" and the agent can pick up the id on
   * the next turn.
   */
  onSaved?: (result: {
    draftId: string
    documentId: string
    title: string
  }) => void
  /** Called after the user clicks Discard. */
  onDiscarded?: (result: { draftId: string }) => void
}

export interface ToolDetailOverlayField {
  label: string
  value?: string | null
  tone?: 'default' | 'critical' | 'high' | 'medium' | 'low' | 'success'
}

export interface ToolDetailOverlaySection {
  title: string
  content?: string | null
}

export interface ToolDetailOverlayPayload {
  entityType:
    | 'risk'
    | 'asset'
    | 'vendor'
    | 'finding'
    | 'ticket'
    | 'policy'
    | 'document'
    | 'claim'
    | 'test'
    | 'evidence'
    | 'identity'
    | 'integration'
    | 'program'
    | 'runbook'
  toolName: string
  title: string
  subtitle?: string | null
  description?: string | null
  fields: ToolDetailOverlayField[]
  sections?: ToolDetailOverlaySection[]
  href?: string | null
}

export interface PolicyDriftReviewOverlayPayload {
  mode?: 'drift' | 'draft'
  title?: string | null
  subtitle?: string | null
  summaryTitle?: string | null
  summaryDescription?: string | null
  sectionTitle?: string | null
  sectionDescription?: string | null
  footerText?: string | null
  policySlug?: string | null
  rationale?: string | null
  confidence?: string | null
  autoApplyEligible?: boolean | null
  sectionEdits: Array<{
    sectionKey?: string | null
    section?: string | null
    replacementText?: string | null
  }>
  claimDiff: Array<{
    action?: string | null
    claimText?: string | null
    controlRef?: string | null
  }>
  onAccepted?: () => Promise<void> | void
  onDiscarded?: () => void
}

export interface SystemDetailOverlayPayload {
  versionId?: string
  entityType:
    | 'control'
    | 'risk'
    | 'policy'
    | 'test'
    | 'submission'
    | 'test-submissions'
    | 'asset'
    | 'vendor'
    | 'document'
  entityId: string
  workspaceId: string
  returnTo?: SystemDetailOverlayPayload
  /**
   * Meaningful for `entityType: 'test'` and `entityType: 'control'`. Defaults
   * to true (read-only preview). Set false when the hosting surface permits
   * inline edits.
   */
  readOnly?: boolean
  /**
   * Only for `entityType: 'test'`. Deep-links to and highlights an auditor
   * comment thread inside the test detail.
   */
  commentId?: string
}

export type MainPanelOverlayEntry =
  | {
      kind: 'vault-document-draft'
      id: string
      source?: 'chat'
      payload: VaultDocumentDraftOverlayPayload
      /**
       * Optional dismiss handler invoked by the shell when the user
       * closes the overlay via the X button or the ESC key. If
       * provided, the shell defers to it instead of calling
       * `closeActive` directly — this lets the opener decide whether
       * a "close without choosing" should be treated the same as a
       * Discard (e.g. so the agent gets a follow-up message and the
       * conversation doesn't stall).
       */
      onDismiss?: () => void
    }
  | {
      kind: 'tool-detail'
      id: string
      source?: 'chat'
      payload: ToolDetailOverlayPayload
    }
  | {
      kind: 'system-detail'
      id: string
      source?: 'chat'
      payload: SystemDetailOverlayPayload
    }
  | {
      kind: 'policy-drift-review'
      id: string
      source?: 'chat'
      payload: PolicyDriftReviewOverlayPayload
    }

export const mainPanelOverlayActiveAtom = atom<MainPanelOverlayEntry | null>(
  null
)

/** Convenience: is any overlay active right now? */
export const mainPanelOverlayHasAnyAtom = atom(
  (get) => get(mainPanelOverlayActiveAtom) !== null
)

/**
 * Set/replace the active overlay. Re-opens with the same id are
 * idempotent (same payload object replaces the prior one) — useful
 * when the chat card's "Reopen draft" button fires after the entry
 * was closed.
 */
export const openMainPanelOverlayAtom = atom(
  null,
  (get, set, entry: MainPanelOverlayEntry) => {
    const active = get(mainPanelOverlayActiveAtom)
    const activeIsDetail =
      active?.kind === 'system-detail' || active?.kind === 'tool-detail'
    const entryIsDetail =
      entry.kind === 'system-detail' || entry.kind === 'tool-detail'
    if (activeIsDetail && entryIsDetail && active) {
      set(mainPanelOverlayActiveAtom, {
        ...entry,
        id: active.id,
        source: entry.source ?? active.source,
      })
      return
    }
    set(mainPanelOverlayActiveAtom, entry)
  }
)

/**
 * Close the active overlay if its id matches. Stale callers (e.g. a
 * chat tool message unmounting after a newer overlay replaced theirs)
 * pass their own id; the no-op-on-mismatch protects the active entry.
 */
export const closeMainPanelOverlayAtom = atom(null, (get, set, id: string) => {
  const active = get(mainPanelOverlayActiveAtom)
  if (active && active.id === id) {
    set(mainPanelOverlayActiveAtom, null)
  }
})

/** Force-close whatever's active. Used by the X icon and ESC key. */
export const closeActiveMainPanelOverlayAtom = atom(null, (_get, set) => {
  set(mainPanelOverlayActiveAtom, null)
})

export const closeChatMainPanelOverlayAtom = atom(null, (get, set) => {
  const active = get(mainPanelOverlayActiveAtom)
  if (active?.source === 'chat') {
    set(mainPanelOverlayActiveAtom, null)
  }
})
